/**
 * A place for caches of "who is signed in" to be cleared from, without the
 * auth provider having to import the modules that hold them.
 *
 * The dependency points this way — each module that caches identity registers
 * its own invalidator when it loads, and the auth provider clears them all
 * without knowing who they are — so that authentication does not depend on
 * the admin service. A module that has not loaded has nothing cached, so there
 * is nothing to miss.
 *
 * Correction, 2026-09-23. This comment used to justify the registry by saying
 * a direct import would pull adminService into the application entry chunk.
 * It was already there: App.tsx imports AdminLayout eagerly, AdminLayout
 * renders AdminSearchProvider, and that imported adminService. The decoupling
 * stands on its own merits; the bundle-size argument for it was wrong.
 *
 * Later the same day the header search stopped importing adminService, so
 * adminService now loads as its own chunk, with the admin pages that use it.
 * The rest of the admin shell still ships to every visitor, public site
 * included — a Phase 3 item in the performance audit.
 */

const invalidators = new Set<() => void>();

/**
 * Register a cache to be cleared whenever the signed-in user may have changed.
 * Call this once, at module scope.
 */
export function registerIdentityCache(invalidate: () => void): void {
  invalidators.add(invalidate);
}

/**
 * Clear every registered identity cache. Call on sign-in, sign-out, and any
 * other point where the caller's identity may no longer be what was resolved.
 */
export function invalidateIdentityCaches(): void {
  for (const invalidate of invalidators) {
    invalidate();
  }
}
